"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useState } from "react"
import { ChevronDown, LogOut, User } from "lucide-react"

import { buttonVariants } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/hooks/useAuth"
import { formatMarkingPlanLine } from "@/lib/marking-plan-label"
import { PROFILE_NAV_GROUPS, SITE_NAV } from "@/lib/site-nav"
import type { AuthUser } from "@/lib/types/auth"
import { cn } from "@/lib/utils"

const BILLING_HREF = "/profile/billing"

function ProfileMenuItems({
  displayName,
  user,
  onLogout,
  compact = false,
}: {
  displayName: string
  user: AuthUser
  onLogout: () => void
  compact?: boolean
}) {
  if (compact) {
    return <MobileAccountMenu onLogout={onLogout} />
  }

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel className="font-medium break-all whitespace-normal text-foreground">
          {displayName}
        </DropdownMenuLabel>
        <DropdownMenuItem render={<Link href={SITE_NAV.profile.href} prefetch={false} />}>
          Панель
        </DropdownMenuItem>
      </DropdownMenuGroup>
      {PROFILE_NAV_GROUPS.map((group) => (
        <DropdownMenuGroup key={group.label}>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
          {group.items.map((item) => (
            <DropdownMenuItem
              key={item.href}
              render={<Link href={item.href} prefetch={false} />}
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        render={<Link href={BILLING_HREF} prefetch={false} />}
        className="mx-1 flex-col items-start gap-0 rounded-lg border border-border bg-muted/40 px-2.5 py-2 hover:border-accent"
      >
        <span className="text-xs text-muted-foreground">Текущий тариф</span>
        <span className="mt-0.5 text-sm font-medium leading-snug">{formatMarkingPlanLine(user)}</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onLogout}>
        <LogOut className="h-4 w-4 shrink-0" />
        Выйти
      </DropdownMenuItem>
    </>
  )
}

function MobileAccountMenu({ onLogout }: { onLogout: () => void }) {
  const pathname = usePathname()
  const accountActive = pathname === SITE_NAV.profile.href || pathname.startsWith("/profile/")
  const [open, setOpen] = useState(accountActive)

  return (
    <>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="min-w-0 flex-1 truncate">Аккаунт</span>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>
      {open ? (
        <>
          <DropdownMenuItem
            className="pl-6"
            render={<Link href={SITE_NAV.profile.href} prefetch={false} />}
          >
            Панель
          </DropdownMenuItem>
          {PROFILE_NAV_GROUPS.flatMap((group) =>
            group.items.map((item) => (
              <DropdownMenuItem
                key={item.href}
                className="pl-6"
                render={<Link href={item.href} prefetch={false} />}
              >
                {item.label}
              </DropdownMenuItem>
            )),
          )}
          <DropdownMenuItem className="pl-6" onClick={onLogout}>
            <LogOut className="h-4 w-4 shrink-0" />
            Выйти
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  )
}

function loginHrefFor(pathname: string | null): string {
  if (!pathname || pathname === "/" || pathname.startsWith("/login")) {
    return SITE_NAV.login.href
  }
  return `${SITE_NAV.login.href}?redirect=${encodeURIComponent(pathname)}`
}

export function SiteAuthControls({
  mobile = false,
  compact = false,
  onBeforeLogin,
}: {
  mobile?: boolean
  compact?: boolean
  onBeforeLogin?: () => void
}) {
  const { user, isAuthenticated, isLoading, logout } = useAuth()
  const loginHref = loginHrefFor(usePathname())

  // Пока сессия поднимается — не даём ссылку «Войти»: иначе proxy/soft-nav
  // при живом auth_session уводил с /login и форма «не открывалась».
  if (isLoading) {
    if (mobile) {
      return (
        <DropdownMenuItem disabled className="pointer-events-none opacity-50">
          <User className="h-4 w-4 shrink-0" />
          Загрузка…
        </DropdownMenuItem>
      )
    }
    return (
      <span
        className={cn(
          buttonVariants({ variant: "ghost", size: "icon-sm" }),
          "pointer-events-none opacity-50",
          !compact && "hidden md:inline-flex",
        )}
        aria-hidden
      >
        <User className="h-4 w-4 shrink-0" />
      </span>
    )
  }

  if (!isAuthenticated || !user) {
    if (mobile) {
      return (
        <>
          <DropdownMenuItem
            render={<Link href={loginHref} prefetch={false} onClick={onBeforeLogin} />}
          >
            {SITE_NAV.login.label}
          </DropdownMenuItem>
          <DropdownMenuItem
            render={<Link href={SITE_NAV.register.href} prefetch={false} onClick={onBeforeLogin} />}
          >
            {SITE_NAV.register.label}
          </DropdownMenuItem>
        </>
      )
    }

    if (compact) {
      return (
        <Link
          href={loginHref}
          prefetch={false}
          onClick={onBeforeLogin}
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          title={SITE_NAV.login.label}
        >
          <User className="h-4 w-4 shrink-0" />
        </Link>
      )
    }

    return (
      <>
        <Link
          href={loginHref}
          prefetch={false}
          onClick={onBeforeLogin}
          className={buttonVariants({
            variant: "default",
            size: "sm",
            className: "hidden gap-1.5 whitespace-nowrap md:inline-flex",
          })}
        >
          {SITE_NAV.login.label}
        </Link>
        <Link
          href={SITE_NAV.register.href}
          prefetch={false}
          onClick={onBeforeLogin}
          className={buttonVariants({
            variant: "info",
            size: "sm",
            className: "hidden gap-1.5 whitespace-nowrap md:inline-flex",
          })}
        >
          {SITE_NAV.register.label}
        </Link>
      </>
    )
  }

  const displayName = user.email?.trim() || user.fullname?.trim() || "Профиль"
  const handleLogout = () => void logout()

  if (mobile) {
    return (
      <ProfileMenuItems displayName={displayName} user={user} onLogout={handleLogout} compact />
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={buttonVariants({
          variant: compact ? "ghost" : "outline",
          size: compact ? "icon-sm" : "default",
          className: compact
            ? undefined
            : "hidden max-w-64 gap-1.5 whitespace-nowrap bg-white shadow-xs md:inline-flex dark:bg-card",
        })}
        title={displayName}
        aria-label={SITE_NAV.profile.label}
      >
        <User className="h-4 w-4 shrink-0" />
        {!compact ? <span className="hidden truncate min-[990px]:inline">{displayName}</span> : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-max min-w-56 max-w-xs">
        <ProfileMenuItems displayName={displayName} user={user} onLogout={handleLogout} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
